import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { getPatients, postPatients, delByPathId, getDuplicatePatients } from "services/api";
import { normalizeDataDuplicatePatients, normalizeDataPatients } from "./operators";

export interface PatientsState {
  patientList: {
    data: any,
    filterableValues: any,
    pagination: {
      total: number,
      page: number,
    }
    status: null | 'idle' | 'loading' | 'failed',
  }
  patientDuplicateList: {
    data: any,
    status: null | 'idle' | 'loading' | 'failed',
  }
}

const initialState: PatientsState = {
  patientList: {
    data: [],
    filterableValues: {},
    pagination: {
      total: 0,
      page: 1,
    },
    status: null,
  },
  patientDuplicateList: {
    data: [],
    status: null,
  },
};

export const getPatientsAsync: any | null = createAsyncThunk(
  'patients/getPatients',
  async (data: any, { getState }) => {
    const state: any = getState();
    const response = await getPatients(data);
    const allServiceStates: any[] = state.masters?.serviceStates?.data ?? [];
    const serviceStateLookup: Record<string, string> = Object.fromEntries(
      allServiceStates.map((s: any) => [s.code, s.label ?? s.code])
    );
    const allServiceTypes: any[] = state.masters?.serviceType?.data ?? [];
    const serviceTypeLookup: Record<string, string> = Object.fromEntries(
      allServiceTypes.map((t: any) => [t.code, t.label ?? t.code])
    );
    return { ...response?.data, serviceStateLookup, serviceTypeLookup };
  },
);

export const getDuplicatePatientsAsync: any | null = createAsyncThunk(
  'patients/getDuplicatePatients',
  async (data: any) => {
    const response = await getDuplicatePatients(data);
    return response;
  },
);

export const postPatientsAsync: any | null = createAsyncThunk(
  'patients/postPatients',
  async (data: any) => {
    const response = await postPatients(data);
    return response;
  },
);

export const delByPathIdPatientsAsync: any | null = createAsyncThunk(
  'patients/delByPathId',
  async (data: any) => {
    const response = await delByPathId(data.path, data.id);
    return response;
  }
);

export const patientsSlice = createSlice({
  name: 'patients',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getPatientsAsync.pending, (state) => {
        state.patientList.status = 'loading';
      })
      .addCase(getPatientsAsync.fulfilled, (state, action) => {
        state.patientList.status = 'idle';
        state.patientList.data = normalizeDataPatients(
          action.payload,
          action.payload?.serviceStateLookup ?? {},
          action.payload?.serviceTypeLookup ?? {},
        );
        state.patientList.filterableValues = action.payload?.item?.filterableValues;
        state.patientList.pagination.total = action.payload?.item?.total;
      })
      .addCase(getPatientsAsync.rejected, (state) => {
        state.patientList.status = 'failed';
      })
      .addCase(getDuplicatePatientsAsync.pending, (state) => {
        state.patientDuplicateList.status = 'loading';
      })
      .addCase(getDuplicatePatientsAsync.fulfilled, (state, action) => {
        state.patientDuplicateList.status = 'idle';
        state.patientDuplicateList.data = normalizeDataDuplicatePatients(action.payload);
      })
      .addCase(getDuplicatePatientsAsync.rejected, (state) => {
        state.patientDuplicateList.status = 'failed';
      })
      .addCase(delByPathIdPatientsAsync.fulfilled, (state, action) => {
        state.patientList.data = state.patientList.data.filter((item: any) => item.id !== action.meta.arg.id)
      })
  }
});

export default patientsSlice.reducer;
